"use client";
import Script from "next/script";
import { useIntersection } from "./use-intersection";
import { createRef, useCallback, useEffect, useState } from "react";

export type MMDataLayerItem = {
  destinationLocations: string | string[] | null;
  homeLocation: string | null;
  languageIsoCode: string;
  originLocations: string[] | null;
};

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- window must be an interface
  interface Window {
    MMdataLayer: MMDataLayerItem[];
  }
}

type LivePricingProperties = {
  contentId: string;
  scriptUrl: string;
};

const once = (function_: () => void) => {
  let hasBeenCalled = false;
  return () => {
    if (hasBeenCalled) {
      return;
    }
    function_();
    hasBeenCalled = true;
  };
};

const onReady = once(() => {
  // eslint-disable-next-line unicorn/prefer-global-this -- disable to get version bump through
  window.MMdataLayer = [
    {
      destinationLocations: null,
      homeLocation: null,
      languageIsoCode: "en",
      originLocations: ["NYC"],
    },
  ];
});

export const LivePricing = ({
  contentId,
  scriptUrl,
}: LivePricingProperties) => {
  const expectedIframeId = `mm${contentId}`;
  const reference = createRef<HTMLDivElement>();
  const { hasIntersected } = useIntersection({
    ref: reference,
    threshold: 0.1,
  });

  const [isLoading, setIsLoading] = useState(true);

  const onLoad = useCallback(
    (event: Event) => {
      const target = event.target as HTMLIFrameElement;
      if (target.id === expectedIframeId) {
        setIsLoading(false);
      }
    },
    [expectedIframeId],
  );

  const onError = useCallback(
    (event: Event) => {
      const target = event.target as HTMLIFrameElement;
      if (target.id === expectedIframeId) {
        setIsLoading(false);
      }
    },
    [expectedIframeId],
  );

  useEffect(() => {
    if (!hasIntersected || !reference.current) {
      return;
    }

    const container = reference.current;

    container.addEventListener("load", onLoad, true);
    container.addEventListener("error", onError, true);

    return () => {
      container.removeEventListener("load", onLoad, true);
      container.removeEventListener("error", onError, true);
    };
  }, [hasIntersected, contentId]);

  return (
    <div data-fs-element="pros" ref={reference}>
      {hasIntersected && (
        <Script
          id="em-container"
          onError={() => {
            setIsLoading(false);
          }}
          onReady={onReady}
          src={scriptUrl}
          strategy="afterInteractive"
        />
      )}
      {isLoading && (
        <div
          className="
            grid grid-cols-1 gap-6

            md:grid-cols-2

            lg:grid-cols-3
          "
        >
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      )}
      <div data-container-id={contentId} />
    </div>
  );
};
export const SkeletonCard = () => (
  <div className="animate-pulse space-y-4 rounded-2xl bg-white p-4 shadow-lg">
    <div className="h-40 rounded-xl bg-off-white" />
    <div className="h-6 w-3/4 rounded-sm bg-off-white" />
    <div className="space-y-2">
      <div className="h-4 w-full rounded-sm bg-off-white" />
      <div className="h-4 w-5/6 rounded-sm bg-off-white" />
      <div className="h-4 w-2/3 rounded-sm bg-off-white" />
    </div>
  </div>
);
